import { supabase as feedbackSupabase } from '../lib/supabase';
import type { SupabaseClient } from '@supabase/supabase-js';

export interface Release {
  id: string;
  title: string;
  artist_id?: string;
  artist_name?: string;
  release_date?: string;
  status?: string;
}

export interface ReleaseSheet {
  id: string;
  created_at: string;
  updated_at: string;
  title: string;
  artist_id: string;
  release_id: string | null;
  release_title: string | null;
  content: any;
  status: 'draft' | 'in_progress' | 'completed' | 'archived';
  tags: string[];
  cover_image_url: string | null;
  due_date: string | null;
  completed_at: string | null;
}

export class ReleaseService {
  // Release Sheets CRUD operations (feedback database)
  static async getReleaseSheets(artistId?: string, client?: SupabaseClient<any>): Promise<ReleaseSheet[]> {
    const supabase = (client || feedbackSupabase) as any;
    let query = supabase
      .from('release_sheets')
      .select('*')
      .order('created_at', { ascending: false });

    if (artistId) {
      query = query.eq('artist_id', artistId);
    }

    const { data, error } = await query;
    
    if (error) {
      console.error('Error fetching release sheets:', error);
      throw error;
    }
    
    return (data || []) as ReleaseSheet[];
  }

  static async getReleaseSheet(id: string, client?: SupabaseClient<any>): Promise<ReleaseSheet | null> {
    const supabase = (client || feedbackSupabase) as any;
    const { data, error } = await supabase
      .from('release_sheets')
      .select('*')
      .eq('id', id)
      .single();
    
    if (error) {
      console.error('Error fetching release sheet:', error);
      throw error;
    }
    
    return data as ReleaseSheet | null;
  }

  static async createReleaseSheet(sheet: Omit<ReleaseSheet, 'id' | 'created_at' | 'updated_at'>, client?: SupabaseClient<any>): Promise<ReleaseSheet> {
    const supabase = (client || feedbackSupabase) as any;
    const { data, error } = await supabase
      .from('release_sheets')
      .insert(sheet)
      .select()
      .single();
    
    if (error) {
      console.error('Error creating release sheet:', error);
      throw error;
    }
    
    return data as ReleaseSheet;
  }

  static async updateReleaseSheet(id: string, updates: Partial<ReleaseSheet>, client?: SupabaseClient<any>): Promise<ReleaseSheet> {
    const supabase = (client || feedbackSupabase) as any;
    const { data, error } = await supabase
      .from('release_sheets')
      .update(updates)
      .eq('id', id)
      .select()
      .single();
    
    if (error) {
      console.error('Error updating release sheet:', error);
      throw error;
    }
    
    return data as ReleaseSheet;
  }

  static async deleteReleaseSheet(id: string, client?: SupabaseClient<any>): Promise<void> {
    const supabase = (client || feedbackSupabase) as any;
    const { error } = await supabase
      .from('release_sheets')
      .delete()
      .eq('id', id);
    
    if (error) {
      console.error('Error deleting release sheet:', error);
      throw error;
    }
  }

  static async getReleases(client?: SupabaseClient<any>): Promise<Release[]> {
    const supabase = (client || feedbackSupabase) as any;
    try {
      const { data: allReleases, error: fetchError } = await supabase
        .from('releases')
        .select('id, name, artist_id, release_date, status')
        .order('release_date', { ascending: false });
      
      if (fetchError) {
        console.error('Error fetching releases:', fetchError);
        return [];
      }
      
      return (allReleases || []).map((release: any) => ({
        id: release.id,
        title: release.name || 'Unknown Release',
        artist_id: release.artist_id,
        release_date: release.release_date,
        status: release.status || 'unknown'
      }));
    } catch (error) {
      console.error('Error fetching releases:', error);
      return [];
    }
  }

  static async getRelease(id: string, client?: SupabaseClient<any>): Promise<Release | null> {
    const supabase = (client || feedbackSupabase) as any;
    try {
      const { data, error } = await supabase
        .from('releases')
        .select('id, name, artist_id, release_date, status')
        .eq('id', id)
        .single();
      
      if (error) {
        console.error('Error fetching release:', error);
        return null;
      }
      
      return {
        id: data?.id,
        title: data?.name || 'Unknown Release',
        artist_id: data?.artist_id,
        release_date: data?.release_date,
        status: data?.status || 'unknown'
      };
    } catch (error) {
      console.error('Error fetching release:', error);
      return null;
    }
  }
}
